import { generatePath } from 'react-router-dom'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { Check } from 'blaze-ui/icons/16'
import MailchimpBrand from 'blaze-ui/icons/35/MailchimpBrand'
import { FacebookBrand, LinkedInBrand } from 'blaze-ui/icons/35'
import { InstagramBrand } from 'blaze-ui/icons/24'
import { TwitterBrand, TikTokBrand, YouTubeBrand, Google } from 'blaze-ui/icons'
import WordPressLogo from 'almanac-ui/icons/24/WordPressLogo'
import Layers5 from 'blaze-ui/icons/24/Layers5'
import LinkExternal from 'blaze-ui/icons/20/LinkExternal'
import LightningFilled from 'blaze-ui/icons/20/LightningFilled'
import X02 from 'blaze-ui/icons/16/X02'
import UserProfileAdd2 from 'blaze-ui/icons/20/UserProfileAdd2'
import { Spinner } from 'common/Spinner/Spinner'
import { useActivationProviders } from 'hooks/queries/integrations/useActivationProviders'
import { useConnectAccount } from 'hooks/useConnectAccount'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { ExternalIntegrationEnum, ActivationProviderKey } from 'api/clients/ExternalIntegrationsClient'
import { CAMPAIGNS_PATH, AUTOPILOT_INTEGRATIONS_PATH } from 'paths'
import Styles from './ActivationConnectModal.module.scss'

const PROVIDER_KEY_TO_ENUM: Partial<Record<ActivationProviderKey, ExternalIntegrationEnum>> = {
  instagram: ExternalIntegrationEnum.Instagram,
  linkedin: ExternalIntegrationEnum.LinkedIn,
  x: ExternalIntegrationEnum.X,
  tiktok: ExternalIntegrationEnum.TikTok,
  youtube: ExternalIntegrationEnum.YouTube,
  word_press: ExternalIntegrationEnum.WordPress,
  mailchimp: ExternalIntegrationEnum.Mailchimp,
  facebook: ExternalIntegrationEnum.Facebook,
  google_business_profile: ExternalIntegrationEnum.GoogleBusinessProfile,
}

const ENUM_TO_ICON: Partial<Record<ExternalIntegrationEnum, typeof InstagramBrand>> = {
  [ExternalIntegrationEnum.Instagram]: InstagramBrand,
  [ExternalIntegrationEnum.LinkedIn]: LinkedInBrand,
  [ExternalIntegrationEnum.X]: TwitterBrand,
  [ExternalIntegrationEnum.TikTok]: TikTokBrand,
  [ExternalIntegrationEnum.YouTube]: YouTubeBrand,
  [ExternalIntegrationEnum.WordPress]: WordPressLogo,
  [ExternalIntegrationEnum.Mailchimp]: MailchimpBrand,
  [ExternalIntegrationEnum.Facebook]: FacebookBrand,
  [ExternalIntegrationEnum.GoogleBusinessProfile]: Google,
}

type ActivationConnectModalProps = {
  onNavigate?: () => void
}

export const ActivationConnectModal = ({ onNavigate }: ActivationConnectModalProps) => {
  const { isMobile } = useResponsiveDesign()
  const { closeModal } = useModals()
  const { data, isLoading } = useActivationProviders()
  const connectAccount = useConnectAccount()
  const workspaceId = useCurrentWorkspaceId()

  if (isMobile) return null

  const providers = data?.providers ?? []

  const handleNavigate = (path: string, params?: Record<string, string>) => {
    onNavigate?.()
    const resolvedPath = generatePath(path, { workspaceId })
    const search = params ? `?${new URLSearchParams(params).toString()}` : ''
    window.open(`${resolvedPath}${search}`, '_blank', 'noopener,noreferrer')
  }

  return (
    <Modal.Root size="sm" blaze onPressOutside={closeModal}>
      <div className={Styles.body}>
        <button type="button" className={Styles.closeButton} onClick={closeModal} aria-label="Close">
          <X02 size={16} color="var(--dark-60)" />
        </button>

        <div className={Styles.inner}>
          <div className={Styles.headline}>
            <div className={Styles.headlineText}>
              <h2 className={Styles.title}>Connect to publish your campaigns</h2>
              <p className={Styles.subtitle}>
                Your campaigns are configured to publish content to these platforms.
                <br />
                Connect your accounts to start auto-publishing and unlock the learning loop.
              </p>
            </div>

            {isLoading ? (
              <Spinner />
            ) : (
              <div className={Styles.providerList}>
                {providers.map((provider) => {
                  const enumValue = PROVIDER_KEY_TO_ENUM[provider.provider as ActivationProviderKey]
                  const Icon = enumValue ? ENUM_TO_ICON[enumValue] : null

                  return (
                    <div key={provider.provider} className={Styles.providerRow}>
                      <div className={Styles.providerLeft}>
                        {Icon && (
                          <span className={Styles.providerIcon}>
                            <Icon size={24} />
                          </span>
                        )}
                        <span className={Styles.providerName}>{provider.display_name}</span>
                      </div>
                      <div className={Styles.providerRight}>
                        {provider.connected ? (
                          <div className={Styles.connectedState}>
                            <span className={Styles.connectedCircle}>
                              <Check size={12} color="white" />
                            </span>
                            <span className={Styles.connectedLabel}>Connected</span>
                          </div>
                        ) : (
                          <>
                            {provider.posts_waiting_count > 0 && (
                              <span className={Styles.waitingPill}>
                                {provider.posts_waiting_count} {provider.posts_waiting_count === 1 ? 'post' : 'posts'}{' '}
                                waiting
                              </span>
                            )}
                            <Button
                              variant="secondary"
                              frontIcon={UserProfileAdd2}
                              onPress={() => enumValue && connectAccount(enumValue)}
                              isDisabled={!enumValue}
                            >
                              Connect
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <hr className={Styles.divider} />

          <div className={Styles.navSection}>
            <button
              type="button"
              className={Styles.navRow}
              onClick={() => handleNavigate(CAMPAIGNS_PATH, { openSettings: 'adjust_defaults' })}
            >
              <Layers5 color="var(--dark-90)" />
              <span className={Styles.navLabel}>Campaign settings</span>
              <div className={Styles.navRight}>
                <span className={Styles.navDescription}>Manage publishing defaults</span>
                <LinkExternal size={20} color="var(--dark-40)" />
              </div>
            </button>
            <button type="button" className={Styles.navRow} onClick={() => handleNavigate(AUTOPILOT_INTEGRATIONS_PATH)}>
              <LightningFilled size={24} color="var(--dark-90)" />
              <span className={Styles.navLabel}>Integrations</span>
              <div className={Styles.navRight}>
                <span className={Styles.navDescription}>See all platforms and accounts</span>
                <LinkExternal size={20} color="var(--dark-40)" />
              </div>
            </button>
          </div>
        </div>
      </div>

      <Modal.Footer>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton variant="primary" onPress={closeModal}>
            Done
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
